import { getProductsAvailable } from 'a/actions/persistStateActions';
import * as SearchlightActions from 'a/actions/searchlightActions';

import AuthService from 'root/lib/authService';
import Env from './Env';
import {
  METRIC_LOGIN_GET_PLACEMENT_ORIGIN_RETRY,
  METRIC_LOGIN_GET_USER_DATA_RETRY,
  METRIC_TYPE,
} from './SearchlightDispatcher';
import { callAsyncWithRetry } from './utilityFunctions';
import {
  NETWORK_ERROR_MESSAGE,
  NO_MCLASS_GRADE_MESSAGE,
  NO_ONBOARDING_GRADE_MESSAGE,
  NO_ONBOARDING_GRADE_SELF_ENROLLED_MESSAGE,
  NO_ONBOARDING_SCHOOL_YEAR_MESSAGE,
  UNAVAILABLE_MESSAGE,
  UNLICENSED_MESSAGE,
  SOMETHING_WENT_WRONG,
} from './messages';
import { API_LICENSES_KEYS, ASSIGNED_PRODUCT_OPTIONS, LICENSE_TYPES, PRODUCTS } from './constants';

export const STUDENT_GRADE_RESTRICTED_ERROR_CODE = 'studentUnlicensedGradeRestricted';
export const NO_MCLASS_GRADE_ERROR_CODE = 'noMclassGrade';
export const NO_ONBOARDING_GRADE_SELF_ENROLLED_ERROR_CODE = 'noOnboardingGradeSelfEnrolled';
export const NO_ONBOARDING_GRADE_ERROR_CODE = 'noOnboardingGrade';
export const NO_ONBOARDING_SCHOOL_YEAR_ERROR_CODE = 'noOnboardingDistrictSchoolYear';
export const NETWORK_ERROR_CODE = 'networkError';

export const LOADING_MESSAGES = {
  LOADING_STATUS_1: 'Hang tight! Checking your info.',
  LOADING_STATUS_2: 'Loading your profile.',
  LOADING_STATUS_3: 'Finishing touches!',
  LOADING_STATUS_4: 'You’re almost Ready!',
  LOADING_STATUS_5: 'Loaded! You’re ready to go!',
};

// loading percentage screen - progress increases as API calls succeed
export const updateLoadingPercentage = (progress, message) => {
  const loadingPercentage = document.getElementById('loadingPercentage');
  const loadingProgress = document.getElementById('loadingProgress');
  const loadingMessage = document.getElementById('loadingMessage');

  loadingPercentage.innerText = `${progress}%`;
  loadingProgress.style.width = `${progress}%`;
  loadingMessage.innerText = message;
};

export const decodeJWT = (token) => {
  const [, base64Token] = token.split('.');
  const base64 = base64Token.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(window.atob(base64));
};

/**
 * Makes a call to the API to verify that the user is licensed to Boost Reading.
 * @param {string} apiVersion target version of the API
 * @param {string} token Keycloak access token
 * @returns fetch that will resolve status code (e.g. 200, 403, or 503)
 */
export const checkLicenses = async (apiVersion, token) => {
  try {
    const response = await fetch(`${Env.API_URL}/users/v${apiVersion}/user/licenses`, {
      method: 'GET',
      credentials: 'include',
      crossDomain: true,
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (e) {
    window.location.href = `${window.location.origin}/error.html?message=${SOMETHING_WENT_WRONG}`;
    throw new Error('Failed `licenseResponse` API call', e);
  }
};

/**
 * Gets the user's aqua id from the API.
 * @param {string} apiVersion target version of the API
 * @param {string} token user's Keycloak token
 * @returns fetch that will resolve UserId
 */
export const getUserId = async (apiVersion, token) => {
  try {
    const response = await fetch(
      `${Env.API_URL}/users/v${apiVersion}/user/id`,
      {
        method: 'GET',
        credentials: 'include',
        crossDomain: true,
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (e) {
    window.location.href = `${window.location.origin}/error.html?message=${SOMETHING_WENT_WRONG}`;
    throw new Error('Failed `userIdResponse` API call', e);
  }
};

export const getHasLicenseValues = (licenses) => {
  const hasReadingLicense = LICENSE_TYPES.includes(licenses[API_LICENSES_KEYS.READING].LicenseType);
  const hasLecturaLicense = LICENSE_TYPES.includes(licenses[API_LICENSES_KEYS.LECTURA].LicenseType);
  const { HasBoostCkla3e: hasCklaLicense, HasBoostEla3e: hasElaLicense } = licenses[API_LICENSES_KEYS.READING];
  const { HasBoostCaminos3e: hasCaminosLicense } = licenses[API_LICENSES_KEYS.LECTURA];
  return [hasReadingLicense, hasLecturaLicense, hasCklaLicense, hasElaLicense, hasCaminosLicense];
};

export const getHasLicenseForGrade = (licenses, readingGrade, lecturaGrade) => {
  const hasReadingLicenseForGrade = licenses[API_LICENSES_KEYS.READING].LicensedGrades.includes(readingGrade);
  const hasLecturaLicenseForGrade = licenses[API_LICENSES_KEYS.LECTURA].LicensedGrades.includes(lecturaGrade);
  return [hasReadingLicenseForGrade, hasLecturaLicenseForGrade];
};

/**
 * Makes a call to the API to get the user's data using
 * the given keycloak bearer token.
 * @param {string} apiVersion target version of the API
 * @param {string} token user's Keycloak token
 * @param {integer} userId the aqua id of the user
 * @returns fetch that will resolve user data
 */
export const getUserData = async (apiVersion, token, userId, product, isAssignmentLink, dispatch) =>
  callAsyncWithRetry(() => fetch(
    `${Env.API_URL}/users/v${apiVersion}/user/${userId}?product=${product}&isDeepLinkAccess=${isAssignmentLink}`, {
      method: 'GET',
      credentials: 'include',
      crossDomain: true,
      headers: {
        authorization: `Bearer ${token}`,
      },
    },
  ),
  {
    logger: async (_, retryAttempts) => {
      const metricCount = retryAttempts.timesAttempted || 1;
      dispatch(
        SearchlightActions.reportMetric(
          METRIC_LOGIN_GET_USER_DATA_RETRY,
          METRIC_TYPE.COUNT,
          metricCount,
          product,
        ),
      );
      if (retryAttempts.isDoneRetrying) {
        await dispatch(SearchlightActions.reportLoginEvent('unavailable'));
        window.location.href = `${window.location.origin}/error.html?message=${UNAVAILABLE_MESSAGE}`;
      }
    },
  });

export const reportAndRedirectError = async (errorCode, dispatch) => {
  const statusesAndMessagesByErrorCode = {
    [STUDENT_GRADE_RESTRICTED_ERROR_CODE]: {
      status: 'unlicensed_grade_restricted',
      message: UNLICENSED_MESSAGE,
    },
    [NO_MCLASS_GRADE_ERROR_CODE]: {
      status: NO_MCLASS_GRADE_MESSAGE,
      message: NO_MCLASS_GRADE_MESSAGE,
    },
    [NO_ONBOARDING_GRADE_ERROR_CODE]: {
      status: NO_ONBOARDING_GRADE_MESSAGE,
      message: NO_ONBOARDING_GRADE_MESSAGE,
    },
    [NO_ONBOARDING_GRADE_SELF_ENROLLED_ERROR_CODE]: {
      status: NO_ONBOARDING_GRADE_SELF_ENROLLED_MESSAGE,
      message: NO_ONBOARDING_GRADE_SELF_ENROLLED_MESSAGE,
    },
    [NO_ONBOARDING_SCHOOL_YEAR_ERROR_CODE]: {
      status: NO_ONBOARDING_SCHOOL_YEAR_MESSAGE,
      message: NO_ONBOARDING_SCHOOL_YEAR_MESSAGE,
    },
    [NETWORK_ERROR_CODE]: {
      status: NETWORK_ERROR_MESSAGE,
      message: NETWORK_ERROR_MESSAGE,
    },
  };

  const errorDetails = statusesAndMessagesByErrorCode[errorCode];
  const status = errorDetails && errorDetails.status;
  const message = errorDetails ? errorDetails.message : UNLICENSED_MESSAGE;
  const redirectUrl = `${window.location.origin}/error.html?message=${message}&continue=true`;
  if (status) {
    await dispatch(SearchlightActions.reportLoginEvent(status));
  }
  window.location.href = redirectUrl;
};

/**
 * Gets the user's adaptivity placement and season data
 * @param {string} apiVersion target version of the API
 * @param {string} token user's Keycloak token
 * @param {integer} product reading or lectura
 * @returns fetch that will resolve placement (including toy) and season data
 */
export const getUserAdaptivity = async (apiVersion, token, product, dispatch) =>
  callAsyncWithRetry(() => fetch(
    `${Env.API_URL}/placement/v${apiVersion}/user/adaptivity?product=${product}`, {
      method: 'GET',
      credentials: 'include',
      crossDomain: true,
      headers: {
        authorization: `Bearer ${token}`,
      },
    },
  ),
  {
    logger: async (_, retryAttempts) => {
      const metricCount = retryAttempts.timesAttempted || 1;
      await dispatch(
        SearchlightActions.reportMetric(
          METRIC_LOGIN_GET_PLACEMENT_ORIGIN_RETRY,
          METRIC_TYPE.COUNT,
          metricCount,
          product,
        ),
      );
      if (retryAttempts.isDoneRetrying) {
        await dispatch(SearchlightActions.reportLoginEvent('unavailable'));
        window.location.href = `${window.location.origin}/error.html?message=${UNAVAILABLE_MESSAGE}`;
      }
    },
  });

export const postUserLoginTimestamp = async (token) => {
  try {
    const now = new Date();

    // even though we don't care if this call fails or not we still need to wait for it so that
    // the call doesn't get killed by entering the app
    const response = await fetch(
      `${Env.API_URL}/users/v${Env.API_VERSION_KEYCLOAK}/user-login`,
      {
        method: 'POST',
        credentials: 'include',
        crossDomain: true,
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ loginTimestamp: now }),
      },
    );

    if (!response.ok) {
      // only check the status in order to log an error--we're not going to prevent the user access
      // if this fails; it's not *that* important
      console.error(`Failed to post user login timestamp with response: ${response}`);
    }
  } catch (e) {
    console.error('Failed to post user login timestamp', e);
  }
};

const postStudentSettings = async (studentId, token, payload) => {
  const GAME_ID = 'STUDENT_SETTINGS';

  try {
    await fetch(
      `${Env.API_URL}/games/v${Env.API_VERSION_KEYCLOAK}/gameState?userId=${studentId}&gameId=${GAME_ID}`,
      {
        method: 'POST',
        credentials: 'include',
        crossDomain: true,
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          [GAME_ID]: payload,
        }),
      },
    );
  } catch (e) {
    console.error(`Failed to post ${GAME_ID}:`, e);
  }
};

export const getStudentSettings = async (studentId, token) => {
  const GAME_ID = 'STUDENT_SETTINGS';

  return fetch(
    `${Env.API_URL}/games/v${Env.API_VERSION_KEYCLOAK}/gameState?userId=${studentId}&gameId=${GAME_ID}`,
    {
      method: 'GET',
      credentials: 'include',
      crossDomain: true,
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

export const getAssignedProduct = async (studentId, token) => {
  const defaultProduct = ASSIGNED_PRODUCT_OPTIONS.STUDENT_CHOICE;
  let product;

  try {
    const response = await getStudentSettings(studentId, token);
    const { STUDENT_SETTINGS } = await response.json();
    product = STUDENT_SETTINGS?.GameData?.product;

    if (STUDENT_SETTINGS?.GameData && !product) {
      const newSettings = {
        ...STUDENT_SETTINGS,
        GameData: {
          ...STUDENT_SETTINGS.GameData,
          product: defaultProduct,
        },
      };
      await postStudentSettings(studentId, token, newSettings);
    }
  } catch (e) {
    console.error('Failed to get assigned product', e);
  }
  return product || defaultProduct;
};

export const getProducts = async (canUseReading, canUseLectura, userId, token) => {
  if (canUseReading && canUseLectura) {
    const assignedProduct = await getAssignedProduct(userId, token);
    switch (assignedProduct) {
      case ASSIGNED_PRODUCT_OPTIONS.LECTURA:
        return PRODUCTS.LECTURA;
      case ASSIGNED_PRODUCT_OPTIONS.STUDENT_CHOICE:
        return `${PRODUCTS.READING},${PRODUCTS.LECTURA}`;
      case ASSIGNED_PRODUCT_OPTIONS.READING:
      default:
        return PRODUCTS.READING;
    }
  } else if (canUseReading) {
    return PRODUCTS.READING;
  } else if (canUseLectura) {
    return PRODUCTS.LECTURA;
  } else {
    return '';
  }
};

/**
 * Sets student data that the React app
 * needs for reporting and such from the given JSON
 * data (retrieved from a successful student login).
 * @param {object} studentData data about a student, recieved from API call
 */
export const setStudentData = ({
  FirstName,
  LastName,
  UserId,
  Organization,
  Grade,
  Groups,
  SchoolYearStart,
  IsTestUser,
  IsStaffLogin,
  Season,
  SeasonLectura,
  Part,
  PartLectura,
  fieldTrial,
  RedirectToIPT,
  RedirectToIPTLectura,
  IPTType,
  IPTTypeLectura,
  IsSpecialContentLicense,
  isFirstLogin,
  isFirstLoginLectura,
  placedMethod,
  placedMethodLectura,
  placementGrade,
  placementGradeLectura,
  placementPeriod,
  placementPeriodLectura,
  productsAvailable,
  skillMap,
  skillMapLectura,
  toy,
  toyLectura,
}) => ({
  firstName: FirstName,
  lastName: LastName,
  studentId: UserId,
  organization: Organization || '',
  group: Groups?.toString() ?? '',
  schoolYearStart: SchoolYearStart,
  grade: Grade,
  sessionId: AuthService.getSessionId(),
  isTestUser: IsTestUser === 'true',
  productsAvailable: getProductsAvailable(productsAvailable),
  isSpecialContentLicense: IsSpecialContentLicense,
  isStaffLogin: IsStaffLogin,
  isFieldTrial: fieldTrial,
  redirectToIpt: RedirectToIPT,
  isPlacementAPT: IPTType === 'APT',
  isPlacementARBA: IPTType === 'TRO-APT',
  redirectToIptLectura: RedirectToIPTLectura,
  isPlacementAPTLectura: IPTTypeLectura === 'APT',
  isPlacementARBALectura: IPTTypeLectura === 'TRO-APT',
  isFirstLogin,
  isFirstLoginLectura,
  placedMethod,
  placedMethodLectura,
  placementGrade,
  placementGradeLectura,
  placementPeriod,
  placementPeriodLectura,
  skillMap,
  skillMapLectura,
  season: localStorage.getItem('localSeason') ?? Season,
  seasonLectura: SeasonLectura,
  seasonPart: Part,
  seasonPartLectura: PartLectura,
  toy,
  toyLectura,
});
